/**
 * Dashboard de lectura rápida: tabla dinámica de leads creados por fecha,
 * con las etapas actuales del espejo Kommo.
 * La sincronización manual usa exclusivamente la acción autenticada syncKommoToSheet(tipo=leads).
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ANALYTICS_CONFIG } from '../../../config/analytics-config.js';
import { loadCohortNow, loadKommoStageOrder, syncCohortSource } from '../services/cohort-service.js';
import { cacheGet, cacheInvalidate, cacheInvalidatePrefix, cacheSet } from '../../../shared/utils/session-cache.js';
import { useAnalyticsRefresh } from '../../../shared/hooks/useAnalyticsRefresh.js';
import { pivotLeads, pivotLeadsByGI, rangeForPreset, sortStagesByKommoOrder, stageIsSelected, tagCatalogForLeads, filterLeadsByTags } from '../utils/pivot-leads.js';
import { validateKommoSyncResult } from '../utils/kommo-sync-result.js';
import { describeKommoSyncProgress, formatSyncDuration } from '../utils/kommo-sync-progress.js';
import { mapAnalyticsSecurityError } from '../../../services/firebase-auth.js';
import './LeadsPivotPage.css';

const COLORS = ['#2563eb', '#0d9488', '#f59e0b', '#8b5cf6', '#e8794b', '#0284c7', '#16a34a', '#d946ef', '#64748b', '#d97706', '#0891b2', '#a855f7'];
const nf = (n) => Number(n || 0).toLocaleString('es-CL');

function prettyDate(value, group) {
  const parts = String(value || '').split('-');
  if (parts.length < 2) return value;
  if (group === 'month') return parts[1] + '/' + parts[0];
  return parts[2] + '/' + parts[1] + (group === 'week' ? ' (sem.)' : '');
}

/**
 * La misma tabla dinámica (Resumen / Por fecha + detalle) se representa en dos
 * secciones independientes para que Kommo y GI sean comparables en una pantalla.
 * GI recibe los nombres del catálogo administrado, nunca labels locales fijos.
 */
/** Desglose dentro de la MISMA fila de estado GI Capital (no panel separado). */
function GIInlineLeads({ leads, title, internalStates, limit, onShowMore }) {
  return (
    <div className="leads-pivot-inline-detail">
      <div className="leads-pivot-inline-detail-heading">
        <strong>{title} · {nf(leads.length)} leads</strong>
        <span>Ordenados por fecha de creación (más recientes primero)</span>
      </div>
      <div className="leads-pivot-table-scroll" role="region"
        aria-label={'Leads asociados a ' + title} tabIndex={0}>
        <table className="leads-pivot-table leads-pivot-detail-table">
          <thead><tr>
            <th scope="col">Lead / nombre</th>
            <th scope="col">ID Kommo</th>
            <th scope="col">Creación</th>
            <th scope="col">Embudo Kommo</th>
            <th scope="col">Etapa Kommo</th>
            <th scope="col">Estado GI Capital</th>
            <th scope="col">Origen / etiquetas</th>
          </tr></thead>
          <tbody>
            {leads.slice(0, limit).map((lead) => {
              const stateName = internalStates.find((state) =>
                String(state.stateId) === String(lead.internalStateId)
              )?.name || lead.internalStateName || 'Sin clasificar';
              return (
                <tr key={lead.leadId}>
                  <th scope="row">
                    <a href={ANALYTICS_CONFIG.kommoLeadUrlTemplate.replace('{id}', encodeURIComponent(lead.leadId))}
                      target="_blank" rel="noopener noreferrer"
                      title={'Abrir lead ' + lead.leadId + ' en Kommo'}>
                      {lead.name || 'Lead ' + lead.leadId} ↗
                    </a>
                  </th>
                  <td className="num">{lead.leadId}</td>
                  <td>{lead.createdAtIso
                    ? new Date(lead.createdAtIso).toLocaleDateString('es-CL', { timeZone: 'America/Santiago' })
                    : '—'}</td>
                  <td>{lead.pipelineName || lead.pipelineId || '—'}</td>
                  <td>{lead.stageName || '—'}</td>
                  <td>{stateName}</td>
                  <td>{[lead.originTag, lead.tagsText].filter(Boolean).filter((value, index, list) =>
                    list.indexOf(value) === index
                  ).join(' · ') || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!leads.length ? <p className="leads-pivot-message">No hay leads asociados con estos filtros.</p> : null}
      </div>
      {leads.length > limit ? (
        <div className="leads-pivot-detail-more">
          <span>Mostrando {nf(Math.min(limit, leads.length))} de {nf(leads.length)} leads.</span>
          <button type="button" className="analytics-btn analytics-btn-ghost"
            onClick={onShowMore}>Mostrar 100 más</button>
        </div>
      ) : null}
    </div>
  );
}

function LeadsStageSection({ source, pivot, group, periodLabel, selected, onToggle, onClearSelection, internalStates }) {
  const [view, setView] = useState('summary');
  const [expanded, setExpanded] = useState(source === 'gi');
  const [detailLimit, setDetailLimit] = useState(100);
  const countPercentage = (count, total) => total
    ? (Math.round((count / total) * 1000) / 10).toLocaleString('es-CL') + '%'
    : '0%';
  const colors = useMemo(
    () => Object.fromEntries(pivot.stages.map((st, i) => [st.id, COLORS[i % COLORS.length]])),
    [pivot.stages]
  );
  const toggle = (date = '', stage = '') => {
    setDetailLimit(100);
    onToggle(source, date, stage);
  };
  const selectedStageName = selected?.stage
    ? pivot.stages.find((st) => st.id === selected.stage)?.label || ''
    : '';
  const selectionLabel = selected?.date ? prettyDate(selected.date, group) : 'Período completo';
  const detail = useMemo(() => {
    if (!selected) return [];
    return pivot.leads
      .filter((lead) =>
        (!selected.date || lead._dateGroup === selected.date) &&
        (!selected.stage || lead._stageKey === selected.stage)
      )
      .sort((a, b) => String(b.createdAtIso || '').localeCompare(String(a.createdAtIso || '')));
  }, [pivot.leads, selected]);

  return (
    <>
          <details className="leads-pivot-panel leads-pivot-distribution" aria-label={source === 'gi' ? 'Distribución por estado GI Capital' : 'Distribución por etapa Kommo'}>
            <summary className="leads-pivot-distribution-summary">
              <span className="leads-pivot-distribution-title">{source === 'gi' ? 'Distribución por estado GI Capital' : 'Distribución por etapa Kommo'}</span>
              <span className="leads-pivot-distribution-total">
                {nf(pivot.total)} leads
                <span className="leads-pivot-distribution-chevron" aria-hidden="true">▾</span>
              </span>
            </summary>
            <div className="leads-pivot-distribution-body">
              <div className="leads-pivot-overview" role="img" aria-label={source === 'gi' ? 'Distribución de leads por estado GI Capital' : 'Distribución porcentual de leads por etapa Kommo'}>
                {pivot.stages.map((s) => (
                  <div key={s.id} style={{ width: (s.count / Math.max(1, pivot.total)) * 100 + '%', background: colors[s.id] }} title={s.label + ': ' + nf(s.count)} />
                ))}
              </div>
              <div className="leads-pivot-legend">
                {pivot.stages.map((s) => (
                  <span key={s.id} title={s.label}>
                    <i style={{ background: colors[s.id] }} />
                    {s.label} <b>{nf(s.count)}</b>
                  </span>
                ))}
                {!pivot.stages.length ? <span>Sin leads para este período.</span> : null}
              </div>
            </div>
          </details>

          <details className="leads-pivot-panel leads-pivot-expandable"
            open={expanded}
            onToggle={(event) => setExpanded(event.currentTarget.open)}
            aria-label={source === 'gi' ? 'Leads por estado GI Capital' : 'Leads por etapa Kommo'}>
            <summary className="leads-pivot-panel-head leads-pivot-expand-summary">
              <h2>Leads por {source === 'gi' ? 'estado GI Capital' : 'etapa Kommo'}</h2>
              <span>{periodLabel} <span className="leads-pivot-chevron" aria-hidden="true">▾</span></span>
            </summary>
            <div className="leads-pivot-tabs" role="tablist" aria-label={source === 'gi' ? 'Ver GI Capital por período o por fecha' : 'Ver Kommo por período o por fecha'}>
              <button type="button" id={'leads-tab-summary-' + source} role="tab" aria-controls={'leads-panel-summary-' + source}
                aria-selected={view === 'summary'} className={view === 'summary' ? 'is-active' : ''}
                onClick={() => { setView('summary'); onClearSelection(); }}>
                Resumen del período
              </button>
              <button type="button" id={'leads-tab-date-' + source} role="tab" aria-controls={'leads-panel-date-' + source}
                aria-selected={view === 'date'} className={view === 'date' ? 'is-active' : ''}
                onClick={() => { setView('date'); onClearSelection(); }}>
                Por fecha
              </button>
            </div>

            {view === 'summary' ? (
              <div id={'leads-panel-summary-' + source} role="tabpanel" aria-labelledby={'leads-tab-summary-' + source} className="leads-pivot-table-scroll">
                <table className="leads-pivot-table leads-pivot-stage-table">
                  <thead><tr>
                    <th scope="col">{source === 'gi' ? 'Estado GI Capital' : 'Etapa actual de Kommo'}</th>
                    <th scope="col" className="num">Leads</th>
                    <th scope="col" className="num">%</th>
                    <th scope="col">Distribución</th>
                  </tr></thead>
                  <tbody>
                    {pivot.stages.map((stage) => {
                      const isGI = source === 'gi';
                      const isSelected = selected?.date === '' && selected?.stage === stage.id;
                      return (
                        <Fragment key={stage.id}>
                          <tr className={isGI ? 'leads-pivot-gi-row' + (isSelected ? ' is-expanded' : '') : ''}
                            onClick={isGI ? () => toggle('', stage.id) : undefined}>
                            <th scope="row">
                              <span className="leads-pivot-dot" style={{ background: colors[stage.id] }} aria-hidden="true" />
                              <button type="button"
                                className={'leads-pivot-stage-link' + (isSelected ? ' is-selected' : '')}
                                aria-expanded={isGI ? isSelected : undefined}
                                aria-controls={isGI ? 'gi-inline-' + stage.id : undefined}
                                onClick={(event) => { event.stopPropagation(); toggle('', stage.id); }}
                                title={'Mostrar ' + nf(stage.count) + ' leads de ' + stage.label}>
                                {stage.label} {isGI ? (isSelected ? '▴' : '▾') : null}
                              </button>
                            </th>
                            <td className="num">
                              <button type="button" className={'leads-pivot-cell' + (isSelected ? ' is-selected' : '')}
                                onClick={(event) => { event.stopPropagation(); toggle('', stage.id); }}
                                title={'Ver ' + nf(stage.count) + ' leads de ' + stage.label}>
                                {nf(stage.count)}
                              </button>
                            </td>
                            <td className="num">{countPercentage(stage.count, pivot.total)}</td>
                            <td>
                              <div className="leads-pivot-singlebar"
                                aria-label={countPercentage(stage.count, pivot.total) + ' de los leads'}>
                                <span style={{ background: colors[stage.id], width: stage.count / Math.max(1, pivot.total) * 100 + '%' }} />
                              </div>
                            </td>
                          </tr>
                          {isGI && isSelected ? (
                            <tr className="leads-pivot-inline-detail-row">
                              <td colSpan={4} id={'gi-inline-' + stage.id}>
                                <GIInlineLeads leads={detail} title={stage.label}
                                  internalStates={internalStates} limit={detailLimit}
                                  onShowMore={() => setDetailLimit((n) => n + 100)} />
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                      );
                    })}
                    {pivot.total > 0 ? (
                      <tr className="leads-pivot-total">
                        <th scope="row">Total</th>
                        <td className="num"><button type="button" className="leads-pivot-cell" onClick={() => toggle() }>{nf(pivot.total)}</button></td>
                        <td className="num">100%</td><td />
                      </tr>
                    ) : null}
                  </tbody>
                </table>
                {!pivot.stages.length ? <p className="leads-pivot-message">Sin leads en el período elegido.</p> : null}
              </div>
            ) : (
              <div id={'leads-panel-date-' + source} role="tabpanel" aria-labelledby={'leads-tab-date-' + source} className="leads-pivot-bydate">
                <div className="leads-pivot-bydate-head">
                  <span>Por {group === 'month' ? 'mes' : group === 'week' ? 'semana' : 'día'} de creación</span>
                  <span>{nf(pivot.total)} leads</span>
                </div>
                {pivot.rows.map((r) => (
                  <details key={r.date} className="leads-pivot-date-group">
                    <summary>
                      <strong>{prettyDate(r.date, group)}</strong>
                      <span className="leads-pivot-date-count">{nf(r.total)} leads</span>
                      <span className="leads-pivot-rowbar" aria-hidden="true">
                        {pivot.stages.map((s) => r.stages[s.id] ? (
                          <span key={s.id} style={{ width: r.stages[s.id] / r.total * 100 + '%', background: colors[s.id] }} />
                        ) : null)}
                      </span>
                      <span className="leads-pivot-chevron" aria-hidden="true">▾</span>
                    </summary>
                    <div className="leads-pivot-table-scroll">
                      <table className="leads-pivot-table leads-pivot-stage-table">
                        <thead><tr>
                          <th scope="col">{source === 'gi' ? 'Estado GI Capital' : 'Etapa actual de Kommo'}</th>
                          <th scope="col" className="num">Leads</th>
                          <th scope="col" className="num">% del día</th>
                          <th scope="col">Distribución</th>
                        </tr></thead>
                        <tbody>
                          {pivot.stages.filter((stage) => r.stages[stage.id]).map((stage) => (
                            <tr key={stage.id}>
                              <th scope="row"><span className="leads-pivot-dot" aria-hidden="true" style={{ background: colors[stage.id] }} />{stage.label}</th>
                              <td className="num">
                                <button type="button" className={'leads-pivot-cell' + (selected?.date === r.date && selected?.stage === stage.id ? ' is-selected' : '')}
                                  onClick={() => toggle(r.date, stage.id)} title={'Ver ' + nf(r.stages[stage.id]) + ' leads · ' + stage.label}>
                                  {nf(r.stages[stage.id])}
                                </button>
                              </td>
                              <td className="num">{countPercentage(r.stages[stage.id], r.total)}</td>
                              <td><div className="leads-pivot-singlebar"><span style={{ background: colors[stage.id], width: r.stages[stage.id] / r.total * 100 + '%' }} /></div></td>
                            </tr>
                          ))}
                          <tr className="leads-pivot-total">
                            <th scope="row">Total</th>
                            <td className="num"><button type="button" className="leads-pivot-cell" onClick={() => toggle(r.date)}>{nf(r.total)}</button></td>
                            <td className="num">100%</td><td />
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </details>
                ))}
                {!pivot.rows.length ? <p className="leads-pivot-message">Sin leads en el rango elegido.</p> : null}
              </div>
            )}
          {selected && !(source === 'gi' && view === 'summary' && selected.stage && !selected.date) ? (
            <section className="leads-pivot-panel" aria-label="Leads de la celda seleccionada">
              <div className="leads-pivot-panel-head">
                <h2>{selectionLabel}{selectedStageName ? ' · ' + selectedStageName : ''} · {nf(detail.length)} leads</h2>
                <button className="analytics-btn analytics-btn-ghost" type="button" onClick={() => onClearSelection()}>Cerrar</button>
              </div>
              <div className="leads-pivot-table-scroll" role="region"
                aria-label={'Detalle de ' + (selectedStageName || 'leads') + ' en tabla'}
                tabIndex={0}>
                <table className="leads-pivot-table leads-pivot-detail-table">
                  <thead>
                    <tr>
                      <th scope="col">Lead / nombre</th>
                      <th scope="col">ID Kommo</th>
                      <th scope="col">Creación</th>
                      <th scope="col">Embudo Kommo</th>
                      <th scope="col">Etapa Kommo</th>
                      <th scope="col">Estado GI Capital</th>
                      <th scope="col">Origen / etiquetas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.slice(0, detailLimit).map((lead) => {
                      const giName = internalStates.find((state) =>
                        String(state.stateId) === String(lead.internalStateId)
                      )?.name || lead.internalStateName || 'Sin clasificar';
                      return (
                        <tr key={lead.leadId}>
                          <th scope="row">
                            <a href={ANALYTICS_CONFIG.kommoLeadUrlTemplate.replace('{id}', encodeURIComponent(lead.leadId))}
                              target="_blank" rel="noopener noreferrer"
                              title={'Abrir lead ' + lead.leadId + ' en Kommo'}>
                              {lead.name || 'Lead ' + lead.leadId} ↗
                            </a>
                          </th>
                          <td className="num">{lead.leadId}</td>
                          <td>{lead.createdAtIso
                            ? new Date(lead.createdAtIso).toLocaleDateString('es-CL', { timeZone: 'America/Santiago' })
                            : '—'}</td>
                          <td>{lead.pipelineName || lead.pipelineId || '—'}</td>
                          <td>{lead.stageName || '—'}</td>
                          <td>{giName}</td>
                          <td>{[lead.originTag, lead.tagsText].filter(Boolean).filter((val, idx, list) =>
                            list.indexOf(val) === idx
                          ).join(' · ') || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {!detail.length ? <p className="leads-pivot-message">No hay leads con estos filtros.</p> : null}
              </div>
              {detail.length > detailLimit ? (
                <div className="leads-pivot-detail-more">
                  <span>Mostrando {nf(Math.min(detailLimit, detail.length))} de {nf(detail.length)} leads.</span>
                  <button type="button" className="analytics-btn analytics-btn-ghost"
                    onClick={() => setDetailLimit((limit) => limit + 100)}>
                    Mostrar 100 más
                  </button>
                </div>
              ) : null}
            </section>
          ) : null}
          </details>
    </>
  );
}

export default function LeadsPivotPage() {
  const [period, setPeriod] = useState('7');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [group, setGroup] = useState('day');
  const [stageOverrides, setStageOverrides] = useState({});
  const [giOverrides, setGiOverrides] = useState({});
  // null = todas las etiquetas (sin filtro); Set vacío = ninguna.
  const [selectedTagKeys, setSelectedTagKeys] = useState(null);
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [stageOrderReloadKey, setStageOrderReloadKey] = useState(0);
  const [stageOrder, setStageOrder] = useState(null);
  const [stageOrderError, setStageOrderError] = useState('');
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncNotice, setSyncNotice] = useState(null);
  const [syncProgressLive, setSyncProgressLive] = useState(null);
  const [syncElapsedMs, setSyncElapsedMs] = useState(0);
  const syncRunInfoRef = useRef(null);
  const syncInFlightRef = useRef(false);
  const pendingSyncRef = useRef(null);

  const range = useMemo(
    () => rangeForPreset(period, customFrom, customTo),
    [period, customFrom, customTo]
  );
  const rangeKey = range ? range.dateFrom + '|' + range.dateTo : '';
  const refresh = useCallback(() => {
    if (!syncInFlightRef.current) setReloadKey((n) => n + 1);
  }, []);
  useAnalyticsRefresh(refresh);

  // Reloj de la sesión actual. El contador de leads sólo avanza cuando
  // Apps Script confirma la escritura del lote en la hoja de preparación.
  useEffect(() => {
    if (!syncBusy) return;
    const tick = () => {
      const started = syncRunInfoRef.current?.startedAtMs;
      if (started) setSyncElapsedMs(Math.max(0, Date.now() - started));
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [syncBusy]);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = 'kommo:stage-order';
    const cached = cacheGet(cacheKey);
    if (Array.isArray(cached?.data)) {
      setStageOrder(cached.data);
      setStageOrderError('');
      return;
    }
    loadKommoStageOrder()
      .then((pipelines) => {
        if (cancelled) return;
        cacheSet(cacheKey, pipelines);
        setStageOrder(pipelines);
        setStageOrderError('');
      })
      .catch((err) => {
        if (cancelled) return;
        setStageOrder(null);
        setStageOrderError(err?.message || 'Orden de Kommo no disponible');
      });
    return () => { cancelled = true; };
  }, [stageOrderReloadKey]);

  // Un solo botón: invoca la API Kommo→Sheets y luego fuerza una lectura nueva.
  // No se usa "Actualizar" para insinuar una sincronización que no ocurrió.
  const handleSyncKommo = useCallback(async () => {
    if (syncInFlightRef.current || loading || !range) return;
    // Una nueva ejecución puede continuar un checkpoint anterior; para
    // calcular velocidad sólo usamos lo procesado DESDE este clic.
    const saved = data?.kommoSync?.progress;
    const resumed = saved && ['running', 'error', 'committing'].includes(saved.status);
    const priorTotal = data?.kommoSync?.coverage === 'complete'
      ? Number(data?.kommoSync?.filasLeads)
      : null;
    const startedAtMs = Date.now();
    syncRunInfoRef.current = {
      startedAtMs,
      initialLeads: resumed ? Number(saved.filasLeads || 0) : 0,
      initialPages: resumed ? Number(saved.paginas || 0) : 0,
      previousCompleteTotal: Number.isFinite(priorTotal) && priorTotal > 0 ? priorTotal : null
    };
    setSyncProgressLive(resumed ? saved : { filasLeads: 0, paginas: 0, completed: false });
    setSyncElapsedMs(0);
    syncInFlightRef.current = true;
    setSyncBusy(true);
    setSyncNotice({
      tone: 'busy',
      text: 'Sincronizando leads desde Kommo hacia el espejo…'
    });
    try {
      let outcome = null;
      // Cada llamada GAS procesa un lote y confirma su checkpoint. No existe
      // ningún límite total de leads: si se interrumpe, el siguiente clic
      // retoma la página indicada por el servidor.
      for (let batch = 0; batch < 2000; batch++) {
        const result = await syncCohortSource({ tipo: 'leads' });
        outcome = validateKommoSyncResult(result);
        setSyncProgressLive(result);
        setSyncElapsedMs(Date.now() - startedAtMs);
        if (!outcome.inProgress) break;
        setSyncNotice({ tone: 'busy', text: outcome.message });
        if (batch === 1999) {
          setSyncNotice({
            tone: 'warning',
            text: outcome.message +
              ' Se alcanzó el máximo de lotes consecutivos de esta sesión. Presioná Continuar sincronización para seguir.'
          });
          syncInFlightRef.current = false;
          setSyncBusy(false);
          return;
        }
      }
      // Sólo después de completed=true se releen métricas; para una API
      // anterior la advertencia no se convierte en certificación de cobertura.
      // La cohorte completa puede estar cacheada en diferentes períodos.
      cacheInvalidatePrefix('cohort:');
      cacheInvalidate('kommo:stage-order');
      setStageOrderReloadKey((n) => n + 1);
      pendingSyncRef.current = outcome;
      setSelected(null);
      setSyncNotice({
        tone: 'busy',
        text: 'Sincronización confirmada. Actualizando cifras y fecha del dashboard…'
      });
      setReloadKey((n) => n + 1);
    } catch (err) {
      const reason =
        mapAnalyticsSecurityError(err) ||
        (err && err.message) ||
        'No se pudo confirmar el resultado de la API.';
      setSyncNotice({
        tone: 'error',
        text:
          'No se pudo confirmar la sincronización: ' + reason +
          ' Si la llamada agotó el tiempo, revisá la fecha de Sync antes de repetirla.'
      });
      syncInFlightRef.current = false;
      setSyncBusy(false);
    }
  }, [loading, range, data]);

  useEffect(() => {
    if (!range) {
      setData(null);
      setError('');
      setLoading(false);
      if (pendingSyncRef.current) {
        pendingSyncRef.current = null;
        setSyncNotice({
          tone: 'warning',
          text: 'La sincronización terminó, pero el período elegido no es válido para actualizar el tablero.'
        });
        syncInFlightRef.current = false;
        setSyncBusy(false);
      }
      return;
    }
    let cancelled = false;
    const cacheKey = 'cohort:' + rangeKey;
    const cached = reloadKey === 0 ? cacheGet(cacheKey) : null;
    if (cached?.data?.cohortLeads) {
      setData(cached.data);
      setError('');
      setLoading(false);
      return;
    }
    setLoading(true);
    setData(null);
    setError('');
    if (reloadKey) cacheInvalidate(cacheKey);
    loadCohortNow(range).then((res) => {
      if (cancelled) return;
      const next = Array.isArray(res?.cohortLeads) ? res : res?.data;
      if (!next || !Array.isArray(next.cohortLeads) || next.success === false) {
        throw new Error(res?.error || 'No se pudieron consultar los leads.');
      }
      if ((next.warnings || []).some((w) => /^Leads:\s/i.test(String(w)))) {
        throw new Error('El espejo de leads Kommo no está disponible.');
      }
      cacheSet(cacheKey, next);
      setData(next);
      if (pendingSyncRef.current) {
        const outcome = pendingSyncRef.current;
        pendingSyncRef.current = null;
        const stillStale = next.kommoSync?.stale === true;
        setSyncNotice({
          tone: outcome.warning || stillStale ? 'warning' : 'success',
          text:
            'Sincronización terminada y dashboard actualizado. ' +
            outcome.message +
            (stillStale
              ? ' La API aún señala el espejo como desactualizado: revisá Sync_Log.'
              : '')
        });
        syncInFlightRef.current = false;
        setSyncBusy(false);
      }
    }).catch((e) => {
      if (cancelled) return;
      const reason = e?.message || 'Error al cargar Kommo.';
      setError(reason);
      if (pendingSyncRef.current) {
        pendingSyncRef.current = null;
        setSyncNotice({
          tone: 'warning',
          text: 'La sincronización terminó, pero no se pudieron leer las cifras nuevas: ' + reason
        });
        syncInFlightRef.current = false;
        setSyncBusy(false);
      }
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [rangeKey, reloadKey]);

  const allLeads = useMemo(() => Array.isArray(data?.cohortLeads) ? data.cohortLeads : [], [data]);
  const internalStates = useMemo(() => Array.isArray(data?.internalStates) ? data.internalStates : [], [data]);
  // Catálogo sobre todos los leads del período, antes de filtrar etapas o estados.
  // Los conteos cuentan cada lead sólo una vez por etiqueta.
  const periodLeads = useMemo(() => pivotLeads(allLeads, range).leads, [allLeads, range]);
  const tagCatalog = useMemo(() => tagCatalogForLeads(periodLeads), [periodLeads]);
  const taggedLeads = useMemo(() => filterLeadsByTags(periodLeads, selectedTagKeys), [periodLeads, selectedTagKeys]);
  const visibleTagKeys = useMemo(
    () => new Set(tagCatalog.filter((tag) => selectedTagKeys === null || selectedTagKeys.has(tag.key)).map((tag) => tag.key)),
    [tagCatalog, selectedTagKeys]
  );
  const changeTag = (key, checked) => {
    setSelectedTagKeys((previous) => {
      const next = previous === null
        ? new Set(tagCatalog.map((tag) => tag.key))
        : new Set(previous);
      if (checked) next.add(key);
      else next.delete(key);
      return next.size === tagCatalog.length ? null : next;
    });
    setSelected(null);
  };
  // El filtro Kommo siempre define el universo; así las vistas Kommo/GI
  // comparten leads y la etapa administrativa Personal conserva su regla
  // original (oculta inicialmente, pero seleccionable por el usuario).
  const stageCatalog = useMemo(
    () => sortStagesByKommoOrder(pivotLeads(taggedLeads, range).stages, stageOrder),
    [taggedLeads, range, stageOrder]
  );
  const visibleStages = useMemo(
    () => new Set(stageCatalog.filter((stage) => stageIsSelected(stage, stageOverrides)).map((stage) => stage.id)),
    [stageCatalog, stageOverrides]
  );
  const kommoUniverse = useMemo(
    () => pivotLeads(taggedLeads, range, 'day', '', visibleStages).leads,
    [taggedLeads, range, visibleStages]
  );
  // El backend ya resuelve el mapeo desde Sales_Stage_Map y aporta tanto el
  // stateId como el catálogo con nombres actualizados de Configuración.
  const giCatalog = useMemo(
    () => pivotLeadsByGI(kommoUniverse, range, 'day', internalStates).stages,
    [kommoUniverse, range, internalStates]
  );
  const visibleGiStates = useMemo(
    () => new Set(giCatalog.filter((state) => giOverrides[state.id] !== false).map((state) => state.id)),
    [giCatalog, giOverrides]
  );
  // Las dos tablas parten del MISMO universo, con el mismo filtro Kommo.
  // Los filtros específicos GI sólo afectan el desglose GI, no la tabla Kommo.
  const kommoPivot = useMemo(() => {
    const result = pivotLeads(kommoUniverse, range, group);
    return { ...result, stages: sortStagesByKommoOrder(result.stages, stageOrder) };
  }, [kommoUniverse, range, group, stageOrder]);
  const giPivot = useMemo(
    () => pivotLeadsByGI(kommoUniverse, range, group, internalStates, visibleGiStates),
    [kommoUniverse, range, group, internalStates, visibleGiStates]
  );
  const changeStage = (id, checked) => {
    setStageOverrides((prev) => ({ ...prev, [id]: checked }));
    setSelected(null);
  };
  const changeGIState = (id, checked) => {
    setGiOverrides((prev) => ({ ...prev, [id]: checked }));
    setSelected(null);
  };
  // Para "Todos" no existe una duración acotada que produzca un promedio
  // diario comparable con 7/30/90 días. No dividir por siglos ficticios.
  const average = period === 'all' ? '—' : kommoPivot.total && range
    ? (() => {
      const ms = new Date(range.dateTo + 'T12:00:00Z') - new Date(range.dateFrom + 'T12:00:00Z');
      return (kommoPivot.total / (Math.round(ms / 86400000) + 1))
        .toLocaleString('es-CL', { maximumFractionDigits: 1 });
    })()
    : '0';
  const periodLabel = period === 'all'
    ? 'Todo el historial disponible'
    : range ? range.dateFrom + ' → ' + range.dateTo : '';
  const toggle = (source, date = '', stage = '') => setSelected((prev) =>
    prev?.source === source && prev?.date === date && prev?.stage === stage
      ? null
      : { source, date, stage }
  );
  const updated = data?.kommoSync?.lastSyncAt
    ? new Date(data.kommoSync.lastSyncAt).toLocaleString('es-CL', { timeZone: 'America/Santiago', dateStyle: 'short', timeStyle: 'short' })
    : '';
  const syncMeta = data?.kommoSync;
  const syncProgress = syncMeta?.progress;
  const resuming = syncProgress && ['running', 'error', 'committing'].includes(syncProgress.status);
  const coverageText = syncMeta?.coverage === 'complete'
    ? 'Recorrido completo de Kommo'
    : syncMeta?.coverage === 'partial'
      ? 'Sincronización parcial (tope anterior)'
      : 'Cobertura no certificada';
  // Mostrar el checkpoint al entrar a la página y, mientras se ejecuta,
  // priorizar la última respuesta confirmada por GAS. Ningún dato
  // descargado en staging se atribuye al espejo ya publicado.
  const progressPayload = syncProgressLive || (resuming ? syncProgress : null);
  const progress = describeKommoSyncProgress(progressPayload, {
    ...(syncRunInfoRef.current || {}),
    elapsedSeconds: syncElapsedMs / 1000
  });
  const showProgress = !!progress && (syncBusy || resuming || !!syncProgressLive);
  const progressLabel = progress?.complete
    ? 'Todos los lotes procesados'
    : syncBusy
      ? progress.pages > 0 ? 'Descargando desde Kommo…' : 'Conectando con Kommo…'
      : 'Descarga pendiente o interrumpida';

  return (
    <div className="analytics-module leads-pivot">
      <header className="leads-pivot-header">
        <div>
          <p className="analytics-eyebrow">Kommo / GI Capital</p>
          <h1 className="analytics-title">Leads por fecha y estado</h1>
          <p className="leads-pivot-sub">
            Mismos leads y fecha de creación → etapas Kommo y estados internos GI Capital.
            {' '}{stageOrder ? 'Orden de Kommo' : stageOrderError ? 'Orden Kommo no disponible; mostrando por cantidad' : 'Consultando orden de Kommo…'}
          </p>
        </div>
        <div className="leads-pivot-filters">
          <label>
            <span>Período</span>
            <select aria-label="Período de creación" disabled={syncBusy} value={period} onChange={(e) => { setPeriod(e.target.value); setSelected(null); }}>
              <option value="7">7 días</option>
              <option value="30">30 días</option>
              <option value="90">90 días</option>
              <option value="all">Todos</option>
              <option value="custom">Personalizado</option>
            </select>
          </label>
          {period === 'custom' ? (
            <>
              <label><span>Desde</span><input aria-label="Desde" type="date" disabled={syncBusy} value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} /></label>
              <label><span>Hasta</span><input aria-label="Hasta" type="date" disabled={syncBusy} value={customTo} onChange={(e) => setCustomTo(e.target.value)} /></label>
            </>
          ) : null}
          <label>
            <span>Agrupar</span>
            <select aria-label="Agrupar fechas" disabled={syncBusy} value={group} onChange={(e) => { setGroup(e.target.value); setSelected(null); }}>
              <option value="day">Día</option>
              <option value="week">Semana</option>
              <option value="month">Mes</option>
            </select>
          </label>
          <div className="leads-pivot-stage-filter">
            <span className="leads-pivot-filter-caption">Etiquetas</span>
            <details>
              <summary aria-label="Filtrar leads por etiquetas de Kommo">
                {selectedTagKeys === null
                  ? 'Todas las etiquetas'
                  : visibleTagKeys.size + ' de ' + tagCatalog.length + ' etiquetas'}
                <span aria-hidden="true">▾</span>
              </summary>
              <div className="leads-pivot-stage-filter-menu">
                <div className="leads-pivot-stage-filter-actions">
                  <button type="button" onClick={() => { setSelectedTagKeys(null); setSelected(null); }}>Todas</button>
                  <button type="button" onClick={() => { setSelectedTagKeys(new Set()); setSelected(null); }}>Ninguna</button>
                </div>
                {tagCatalog.map((tag) => (
                  <label key={tag.key} className="leads-pivot-stage-option">
                    <input type="checkbox" checked={visibleTagKeys.has(tag.key)}
                      onChange={(event) => changeTag(tag.key, event.target.checked)} />
                    <span>{tag.name}</span>
                    <small>{nf(tag.count)}</small>
                  </label>
                ))}
                {!tagCatalog.length ? <p>Sin etiquetas en este período.</p> : null}
              </div>
            </details>
          </div>
          <div className="leads-pivot-stage-filter">
            <span className="leads-pivot-filter-caption">Etapas Kommo</span>
            <details>
              <summary aria-label="Filtrar por etapas de Kommo">
                {data ? visibleStages.size + ' de ' + stageCatalog.length + ' seleccionadas' : 'Seleccionar etapas'}
                <span aria-hidden="true">▾</span>
              </summary>
              <div className="leads-pivot-stage-filter-menu">
                <div className="leads-pivot-stage-filter-actions">
                  <button type="button" onClick={() => {
                    setStageOverrides(Object.fromEntries(stageCatalog.map((stage) => [stage.id, true])));
                    setSelected(null);
                  }}>Todas</button>
                  <button type="button" onClick={() => { setStageOverrides({}); setSelected(null); }}>Restablecer</button>
                </div>
                {stageCatalog.map((stage) => (
                  <label key={stage.id} className="leads-pivot-stage-option">
                    <input type="checkbox" checked={visibleStages.has(stage.id)}
                      onChange={(e) => changeStage(stage.id, e.target.checked)} />
                    <span>{stage.label}</span>
                    <small>{nf(stage.count)}</small>
                  </label>
                ))}
                {!stageCatalog.length ? <p>Sin etapas en este período.</p> : null}
              </div>
            </details>
          </div>
          <div className="leads-pivot-stage-filter">
              <span className="leads-pivot-filter-caption">Estados GI Capital</span>
              <details>
                <summary aria-label="Filtrar por estados internos GI Capital">
                  {visibleGiStates.size + ' de ' + giCatalog.length + ' seleccionados'}
                  <span aria-hidden="true">▾</span>
                </summary>
                <div className="leads-pivot-stage-filter-menu">
                  <div className="leads-pivot-stage-filter-actions">
                    <button type="button" onClick={() => { setGiOverrides({}); setSelected(null); }}>Todos</button>
                    <button type="button" onClick={() => {
                      setGiOverrides(Object.fromEntries(giCatalog.map((state) => [state.id, false])));
                      setSelected(null);
                    }}>Ninguno</button>
                  </div>
                  {giCatalog.map((state) => (
                    <label key={state.id} className="leads-pivot-stage-option">
                      <input type="checkbox" checked={visibleGiStates.has(state.id)}
                        onChange={(e) => changeGIState(state.id, e.target.checked)} />
                      <span>{state.label}</span>
                      <small>{nf(state.count)}</small>
                    </label>
                  ))}
                  {!giCatalog.length ? <p>No hay estados internos para este período.</p> : null}
                </div>
              </details>
          </div>
        </div>
      </header>

      {period === 'custom' && !range ? <p className="leads-pivot-message">Seleccioná ambas fechas, con Desde anterior o igual a Hasta.</p> : null}
      {period === 'all' ? (
        <p className="leads-pivot-source-note">
          Todo el historial disponible en el espejo Kommo con fecha de creación.
          {' '}Se mantienen los filtros de etapas y estados seleccionados.
          {' '}La cobertura completa depende del resultado de la última sincronización.
        </p>
      ) : null}
      {loading ? <p className="leads-pivot-message" role="status">Cargando leads…</p> : null}
      {error ? <p className="analytics-error-inline" role="alert">{error} <button className="analytics-btn analytics-btn-ghost" onClick={refresh}>Reintentar</button></p> : null}
      {data && range ? (
        <>
          <div className="leads-pivot-kpis">
            <article><span>Leads creados</span><strong>{nf(kommoPivot.total)}</strong></article>
            <article><span>Etapas Kommo</span><strong>{nf(kommoPivot.stages.length)}</strong></article>
            <article><span>Promedio por día</span><strong>{average}</strong></article>
          </div>

          <LeadsStageSection
            source="kommo"
            pivot={kommoPivot}
            periodLabel={periodLabel}
            group={group}
            selected={selected?.source === 'kommo' ? selected : null}
            onToggle={toggle}
            onClearSelection={() => setSelected(null)}
            internalStates={internalStates}
          />
          {giPivot.total !== kommoPivot.total ? (
            <p className="leads-pivot-source-note" role="status">
              El filtro de estados GI Capital muestra {nf(giPivot.total)} de {nf(kommoPivot.total)} leads.
              Seleccioná «Todos» en Estados GI Capital para comparar los totales.
            </p>
          ) : null}
          <LeadsStageSection
            source="gi"
            pivot={giPivot}
            periodLabel={periodLabel}
            group={group}
            selected={selected?.source === 'gi' ? selected : null}
            onToggle={toggle}
            onClearSelection={() => setSelected(null)}
            internalStates={internalStates}
          />

        </>
      ) : null}

      <section className="leads-pivot-sync-area" aria-label="Actualización de leads Kommo">
        <div className="leads-pivot-sync-row">
          <span>
            Estados actuales, no históricos · Fuente: espejo Kommo · Última sincronización:{' '}
            <strong>{updated || 'sin fecha registrada'}</strong>
            {' · '}{syncMeta?.filasLeads != null ? nf(syncMeta.filasLeads) + ' leads' : 'cantidad desconocida'}
            {' · '}<strong>{coverageText}</strong>
          </span>
          <button
            type="button"
            className="analytics-btn leads-pivot-sync-button"
            onClick={handleSyncKommo}
            disabled={syncBusy || loading || !range}
            aria-busy={syncBusy ? 'true' : 'false'}
            title="Sincronizar los leads desde Kommo hacia la planilla espejo y actualizar el dashboard"
          >
            {syncBusy ? 'Sincronizando…' : resuming ? 'Continuar sincronización' : 'Sincronizar Kommo'}
          </button>
        </div>
        {showProgress ? (
          <div className="leads-pivot-progress" aria-label="Avance de la sincronización Kommo">
            <div className="leads-pivot-progress-heading">
              <strong>{progressLabel}</strong>
              <span>
                {progress.complete
                  ? '100 % · Finalizado'
                  : progress.estimatedPercent != null
                    ? '≈ ' + progress.estimatedPercent + ' % de la referencia anterior'
                    : 'Total por descubrir'}
              </span>
            </div>
            <div
              className="leads-pivot-progress-track"
              role="progressbar"
              aria-label="Leads descargados desde Kommo"
              aria-valuemin={progress.estimatedPercent != null || progress.complete ? 0 : undefined}
              aria-valuemax={progress.estimatedPercent != null || progress.complete ? 100 : undefined}
              aria-valuenow={progress.complete ? 100 : progress.estimatedPercent ?? undefined}
              aria-valuetext={
                nf(progress.leads) + ' leads, ' + nf(progress.pages) +
                ' páginas; ' + (progress.complete ? 'finalizado' : 'total pendiente de conocer')
              }
            >
              <span
                className={
                  'leads-pivot-progress-fill' +
                  (progress.indeterminate ? ' is-indeterminate' : '') +
                  (progress.complete ? ' is-complete' : '')
                }
                style={!progress.indeterminate ? { width: (progress.complete ? 100 : progress.estimatedPercent ?? 0) + '%' } : undefined}
              />
            </div>
            <div className="leads-pivot-progress-stats">
              <span><strong>{nf(progress.leads)}</strong> leads recuperados</span>
              <span><strong>{nf(progress.pages)}</strong> páginas procesadas</span>
              {syncRunInfoRef.current ? (
                <span><strong>{formatSyncDuration(progress.elapsedSeconds)}</strong> transcurridos</span>
              ) : null}
              {progress.leadsPerMinute != null && progress.leadsPerMinute > 0 ? (
                <span><strong>≈ {nf(Math.round(progress.leadsPerMinute))}</strong> leads/min</span>
              ) : null}
            </div>
            {!progress.complete ? (
              <p className="leads-pivot-progress-note">
                {progress.estimatedRemainingSeconds != null
                  ? 'Tiempo restante aproximado: ' + formatSyncDuration(progress.estimatedRemainingSeconds) +
                    ' (≈ ' + nf(progress.estimatedRemainingLeads) +
                    ' leads hasta el total del último recorrido completo: ' +
                    nf(progress.previousCompleteTotal) + '). Puede variar si Kommo cambió.'
                  : 'Kommo no informa un total confirmado al comenzar. El avance y las páginas son reales; todavía no hay un tiempo restante confiable.'}
                {' '}Los leads recuperados siguen en preparación hasta finalizar.
              </p>
            ) : (
              <p className="leads-pivot-progress-note">
                Paginación finalizada. Se está mostrando el resultado confirmado por el backend.
              </p>
            )}
          </div>
        ) : null}
        <div aria-live="polite" aria-atomic="true">
          {resuming && !syncBusy && !syncNotice ? (
            <p className="leads-pivot-sync-message is-warning">
              Sincronización pendiente: {nf(syncProgress.filasLeads)} leads ya descargados en staging.
              {' '}El espejo actual sigue disponible; presioná «Continuar sincronización».
              {syncProgress.error ? ' Último error: ' + syncProgress.error : ''}
            </p>
          ) : null}
          {syncNotice ? (
            <p className={'leads-pivot-sync-message is-' + syncNotice.tone} role="status">
              {syncNotice.text}
            </p>
          ) : null}
          {data?.kommoSync?.stale && !syncBusy ? (
            <p className="leads-pivot-sync-message is-warning">
              El espejo está desactualizado. Presioná «Sincronizar Kommo» para traer las etapas actuales.
            </p>
          ) : null}
        </div>
        <div className="leads-pivot-sync-detail">
          <Link to="/cohorte-detalle">Ver análisis detallado →</Link>
        </div>
      </section>
    </div>
  );
}
